import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import {
  AppCard,
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppModal,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
} from "@/layout/AppComponents";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import Moment from "moment";
import { axiosPath } from "../../../../routing/helpers/router";
import Translator from "../../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../../layout/components/breadcrumb/BreadcrumbHandler";
import AppSubmitButton from "../../../../layout/components/form/AppSubmitButton";
import errorHandler from "@/layout/components/error/ErrorHandler";
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import ModuleComsaEnvironmentalSideBarReports from "../ModuleComsaEnvironmentalSideBarReports";
import ppiInspectionDefaultValues from "./constants/constructionPpiDefaultValues";
import ModuleComsaEnvironmentalConstructionPpiCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalConstruction/ModuleComsaEnvironmentalConstructionPpiCard";
import ModuleComsaEnvironmentalTitleCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard";
import AppButton from "../../../../layout/components/form/AppButton";
import AppLink from "../../../../routing/helpers/AppLink";
import { toFormData } from "../../../../minions/forms";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";
import _ from "lodash";

export default function ModuleComsaEnvironmentalConstructionPpiScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const auth = useSelector((state) => state.auth);
  const global = useSelector((state) => state.global);
  const [initialValues, setInitialValues] = useState();
  const [filterApplicable, setFilterApplicable] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportMilestones, setReportMilestones] = useState([]);
  const [responsibleOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="supervisor" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="site.manager" />
      ),
      value: 1,
    },
  ]);
  const [frequencyOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="daily.report" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="weekly.report" />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="monthly.report" />
      ),
      value: 2,
    },
  ]);

  const transPpiInspectionDefaultValues = useCallback(
    (ppi) =>
      ppi.map((idv) => ({
        ...idv,
        description: ReactDOMServer.renderToString(
          <Translator file="module-comsaEnvironmental" name={idv.description} />
        ),
        rule: ReactDOMServer.renderToString(
          <Translator file="module-comsaEnvironmental" name={idv.rule} />
        ),
        inspectionType: ReactDOMServer.renderToString(
          <Translator
            file="module-comsaEnvironmental"
            name={idv.inspectionType}
          />
        ),
        acceptanceRejection: ReactDOMServer.renderToString(
          <Translator
            file="module-comsaEnvironmental"
            name={idv.acceptanceRejection}
          />
        ),
      })),
    []
  );

  const [inspectionDefaultValues] = useState(
    transPpiInspectionDefaultValues(ppiInspectionDefaultValues)
  );

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "constructionPpi",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues({
              inspection:
                r.data?.sheetData?.inspection ?? inspectionDefaultValues,
              observations: r.data?.sheetData?.observations ?? [],
              automaticApplicable:
                r.data?.sheetData?.automaticApplicable ?? null,
              applicable: r.data?.sheetData?.applicable ?? null,
              milestones: r.data?.sheetData?.milestones ?? [],
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "constructionPpi",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  // > REPORT MILESTONE //
  const reportUploadValidationSchema = Yup.object().shape({
    name: Yup.string().when("files", ([files], schema) => {
        if (files && files.length > 0) {
            return schema.required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />));
            }
            return schema;
        }),
  });

  const loadReportMilestones = useCallback(async () => {
    await axios
      .post(
        axiosPath(
          "api_r_user_module_comsaEnvironmental_listXlsPpiReportMilestone"
        ),
        { moduleBridgeHex, subModuleBridgeHex, plantHex }
      )
      .then((r) => {
        if (isMounted.current && !_.isEmpty(r.data)) {
          setReportMilestones(r.data);
        }
      });
  }, [moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    loadReportMilestones();
  }, []);

  const uploadReportMilestone = async (values) => {
    await axios
      .post(
        axiosPath(
          "api_r_user_module_comsaEnvironmental_editXlsPpiReportMilestone"
        ),
        toFormData({
          ...values,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          selectedSectionName: "construction",
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((r) => {
        if (isMounted.current) {
          setShowReportModal(false);
          setReportMilestones(r.data);
        }
      })
      .catch((e) => {
        dispatch(errorHandler(e));
      });
  };

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt)) {
      setFieldValue(
        "deletedFiles",
        deletedFiles.filter((s) => s !== salt)
      );
    } else setFieldValue("deletedFiles", [...deletedFiles, salt]);
  };
  // < REPORT MILESTONE //

  const renderCard = useCallback(
    (values, setFieldValue) =>
      inspectionDefaultValues.map((_, i) => (
        <React.Fragment key={i}>
          <ModuleComsaEnvironmentalConstructionPpiCard
            index={i}
            filterApplicable={filterApplicable}
            responsibleOptions={responsibleOptions}
            frequencyOptions={frequencyOptions}
            inspectionDefaultValues={inspectionDefaultValues}
            values={values}
            setFieldValue={(n, v) => setFieldValue(n, v)}
          />
        </React.Fragment>
      )),
    [
      inspectionDefaultValues,
      filterApplicable,
      responsibleOptions,
      frequencyOptions,
    ]
  );

  const handleTranslations = useCallback(
    (values, setValues) => {
      const tempInspectionDefaultValues = inspectionDefaultValues.map(
        (idv) => ({
          ...idv,
          description: ReactDOMServer.renderToString(
            <Translator
              file="module-comsaEnvironmental"
              name={idv.description}
            />
          ),
          rule: ReactDOMServer.renderToString(
            <Translator file="module-comsaEnvironmental" name={idv.rule} />
          ),
          inspectionType: ReactDOMServer.renderToString(
            <Translator
              file="module-comsaEnvironmental"
              name={idv.inspectionType}
            />
          ),
          acceptanceRejection: ReactDOMServer.renderToString(
            <Translator
              file="module-comsaEnvironmental"
              name={idv.acceptanceRejection}
            />
          ),
        })
      );

      setValues({
        ...values,
        inspection: tempInspectionDefaultValues,
      });
    },
    [inspectionDefaultValues]
  );

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  const handleReport = () => axios
    .post(
        axiosPath('api_r_user_module_comsaEnvironmental_downloadXlsPpiReport'),
        {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            section: moduleData?.selectedSection?.name,
        }
    )
    .then(({ data }) => (window.location = data))
    .catch((e) => dispatch(errorHandler(e)))

  const getXlsPpiReportMilestoneLink = (id, salt) => axios
    .post(
        axiosPath('api_r_user_module_comsaEnvironmental_getXlsPpiReportMilestoneLink'),
        {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            selectedSectionName: moduleData?.selectedSection?.name,
            id,
            salt,
        }
    )
    .then(({ data }) => (window.location = data))
    .catch((e) => dispatch(errorHandler(e)))

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="construction"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_industrial_ppi" />
        <div className="row">
          <div className="col-12">
            <Title className="mb-4">
              <Translator
                file="module-comsaEnvironmental"
                name="inspection.checkpoints.program"
              />
            </Title>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({
              setValues,
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="d-flex justify-content-between mt-2">
                  <FilterApplicable
                    className="mb-2"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="filter.applicable"
                      />
                    )}
                    onClick={() => setFilterApplicable(!filterApplicable)}
                    $filtered={filterApplicable}
                  >
                    <AppRemixIcon icon="filter" className="mr-2" size={16} />
                    {filterApplicable ? (
                      <Translator
                        file="module-comsaEnvironmental"
                        name="remove.applicable.filter"
                      />
                    ) : (
                      <Translator
                        file="module-comsaEnvironmental"
                        name="filter.applicable"
                      />
                    )}
                  </FilterApplicable>
                  <div>
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="download.report"
                        />
                      )}
                    >
                        <span
                            className="btn btn-sm btn-info mr-2"
                            onClick={handleReport}
                            >
                            <AppRemixIcon icon="download" />
                        </span>
                    </span>
                    <AppButton
                      color="warning"
                      icon="file-excel-2"
                      onClick={() => setShowReportModal(true)}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="reports"
                        />
                      )}
                    />
                  </div>
                </div>
                {!_.isEqual(values?.inspection, inspectionDefaultValues) && (
                  <>
                    <AppResetValues
                      className="mt-2 mb-2"
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="reset.all"
                        />
                      )}
                      onClick={() => handleTranslations(values, setValues)}
                    >
                      <AppRemixIcon
                        icon="arrow-go-back"
                        className="mr-2"
                        size={16}
                      />
                      <Translator
                        file="module-comsaEnvironmental"
                        name="reset.all"
                      />
                    </AppResetValues>
                    <div className="clearfix" />
                  </>
                )}
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="atmospheric.emissions"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(0, 7)}
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="noise.vibrations"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(7, 8)}
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="liquid.discharges"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(8, 12)}
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="riverbed.affections"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(12, 14)}
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="soil.affection"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(14, 22)}
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="marine.coastal.environment"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(22, 27)}
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="vegetation.affection"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(27, 28)}
                <ModuleComsaEnvironmentalTitleCard color="danger">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="wildlife.affection"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(28, 29)}
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="natural.resources.consumption"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(29, 32)}
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator file="module-comsaEnvironmental" name="waste" />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(32, 123)}
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="order.cleanliness.conditions"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(123, 125)}
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="landscape"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(125, 126)}
                <ModuleComsaEnvironmentalTitleCard color="primary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="historical.artistic.heritage.affections"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(126, 128)}
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="socioeconomic.impact"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(128, 130)}
                <AppCard className="mb-2">
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th className="col-sm-2">
                            <Translator
                              file="module-comsaEnvironmental"
                              name="date"
                            />
                          </th>
                          <th className="col-sm-4">
                            <Translator
                              file="module-comsaEnvironmental"
                              name="user"
                            />
                          </th>
                          <th className="col-sm-4">
                            <Translator
                              file="module-comsaEnvironmental"
                              name="description"
                            />
                          </th>
                          <th className="col-sm-2 text-center">
                            <Translator
                              file="module-comsaEnvironmental"
                              name="options"
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <FieldArray
                          name="observations"
                          render={(arrayHelpers) => (
                            <>
                              {values?.observations &&
                                values?.observations.map((row, i) => (
                                  <tr key={i}>
                                    <td>
                                      <AppDatePicker
                                        title={ReactDOMServer.renderToString(
                                          <Translator
                                            file="module-comsaEnvironmental"
                                            name="date"
                                          />
                                        )}
                                        value={row?.date}
                                        error={errors?.date}
                                        onChange={(date) =>
                                          setFieldValue(
                                            `observations[${i}].date`,
                                            date
                                          )
                                        }
                                        placeholder="dd/mm/yyyy"
                                        showMonthDropdown
                                        showYearDropdown
                                      />
                                    </td>
                                    <td>
                                      <AppInput
                                        title={ReactDOMServer.renderToString(
                                          <Translator
                                            file="module-comsaEnvironmental"
                                            name="user"
                                          />
                                        )}
                                        onChange={(v) =>
                                          setFieldValue(
                                            `observations[${i}].user`,
                                            v
                                          )
                                        }
                                        value={row?.user}
                                        isReadOnly
                                      />
                                    </td>
                                    <td>
                                      <AppInput
                                        title={ReactDOMServer.renderToString(
                                          <Translator
                                            file="module-comsaEnvironmental"
                                            name="description"
                                          />
                                        )}
                                        onChange={(v) =>
                                          setFieldValue(
                                            `observations[${i}].description`,
                                            v
                                          )
                                        }
                                        value={row?.description}
                                        isClearable
                                      />
                                    </td>
                                    <td className="text-center align-middle">
                                      <span
                                        className="btn btn-danger btn-xs"
                                        onClick={() =>
                                          setFieldValue(
                                            "observations",
                                            values?.observations.filter(
                                              (n, index) => index !== i
                                            )
                                          )
                                        }
                                        title={ReactDOMServer.renderToString(
                                          <Translator
                                            file="module-comsaReports"
                                            name="delete"
                                          />
                                        )}
                                      >
                                        <AppRemixIcon icon="delete-bin-6" />
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              <tr>
                                <td colSpan={3} />
                                <td className="text-center align-middle">
                                  <span
                                    className="btn btn-success btn-xs"
                                    onClick={() =>
                                      arrayHelpers.push({
                                        date: Moment().format("DD/MM/YYYY"),
                                        user: auth?.user?.user,
                                        description: "",
                                      })
                                    }
                                  >
                                    <Translator
                                      file="module-comsaEnvironmental"
                                      name="add"
                                    />
                                  </span>
                                </td>
                              </tr>
                            </>
                          )}
                        />
                      </tbody>
                    </table>
                  </div>
                </AppCard>
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
      <AppModal
        show={showReportModal}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaEnvironmental" name="reports" />
        )}
        onClose={() => setShowReportModal(false)}
      >
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
          initialValues={{
            name: "",
            files: null,
            deletedFiles: [],
          }}
          validationSchema={reportUploadValidationSchema}
          onSubmit={uploadReportMilestone}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <>
              <div className="row">
                <div className="col-lg-12">
                  <div className="mb-3">
                    <AppDatePicker
                      title={
                        <Translator
                          file="module-comsaEnvironmental"
                          name="date"
                        />
                      }
                      value={values.name}
                      onChange={(opt) => setFieldValue("name", opt)}
                      minDate={new Date(2021, 0, 1)}
                      dateFormat="yyyy-MM"
                      displayDateFormat="YYYY-MM"
                      placeholder={
                        <Translator
                          file="module-comsaEnvironmental"
                          name="select.a.date"
                        />
                      }
                      isClearable
                      showMonthYearPicker
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      error={errors.name}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <label>
                    <Translator file="module-comsaReports" name="attach" />
                  </label>
                  <AppFileUploader
                    placeholder={
                      !values.files || Object.keys(values?.files).length === 0
                        ? ReactDOMServer.renderToString(
                            <Translator file="general" name="select.a.file" />
                          )
                        : ReactDOMServer.renderToString(
                            <Translator
                              file="general"
                              name="one.file.selected"
                            />
                          )
                    }
                    onChange={(opt) => setFieldValue("files", opt)}
                    btnText={<Translator file="general" name="search..." />}
                  />
                  <div className="mb-3">
                    {!_.isEmpty(reportMilestones) && (
                      <div className="row">
                        <div className="col-12">
                          <label>
                            <Translator
                              file="module-comsaReports"
                              name="delete"
                            />
                          </label>
                          {reportMilestones.map((r, index) => (
                            <div key={index} className="mb-3">
                              <label className="mb-2 pointer font-weight-light">
                                <input
                                  type="checkbox"
                                  className="mr-1"
                                  value={r.files.salt}
                                  onChange={() =>
                                    handleDeletedFiles(
                                      setFieldValue,
                                      values.deletedFiles,
                                      r.files.salt
                                    )
                                  }
                                  checked={
                                    values.deletedFiles &&
                                    values.deletedFiles[r.files.salt]
                                  }
                                />
                                {r.files.name}.{r.files.extension}
                              </label>
                              <span
                                    className="btn btn-sm btn-outline-info float-right"
                                    title={ReactDOMServer.renderToString(
                                        <Translator
                                            file="module-comsaReports"
                                            name="download.report"
                                        />
                                    )}
                                    onClick={() => getXlsPpiReportMilestoneLink(r.id, r.files.salt)}
                                >
                                        <AppRemixIcon icon="download" />
                                </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-block"
                    type="submit"
                    onClick={handleSubmit}
                    disabled={
                      isSubmitting ||
                      ((values?.name?.length === 0 || values?.files === null) &&
                        values?.deletedFiles?.length === 0)
                    }
                  >
                    <Translator file="module-comsaReports" name="edit" />
                  </button>
                </div>
              </div>
            </>
          )}
        </Formik>
      </AppModal>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;

const FilterApplicable = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background-color: #28a745;
  color: #fff;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid #28a745;
  color: ${(props) => (props.$filtered ? '#28a745' : '#fff')};
  background-color: ${(props) => (props.$filtered ? '#fff' : '#28a745')};
`;

const AppResetValues = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background-color: #ffb351;
  color: #fff;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  float: right;
`;
